import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';
import { Therapist } from '../../types/clinician';

interface TherapistState {
  therapist: Therapist[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  loading: boolean;
  zipCode: string;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  } | null;
}

const initialState: TherapistState = {
  therapist: [],
  status: 'idle',
  error: null,
  loading: false,
  zipCode: '',
  pagination: null,
};

// Define the parameters for the fetchTherapists thunk
interface FetchTherapistsParams {
  search?: string; // Backend expects 'search', not 'query'
  speciality?: string; // Backend expects 'speciality', not 'specialty'
  page?: number; // Optional page parameter
  limit?: number; // Optional limit parameter
  zipCode?: string; // Backend expects 'zipCode'
  availability?: string; // Backend supports availability filtering
  insurance?: string;
  sessionRate?: string;
  /** JSON string: { isActive: true, sessionTypes: string[] } */
  sessionTypes?: string;
  /** JSON string: { isActive: true, sessionModes: string[] } */
  sessionMode?: string;
  /** Comma-separated list (matches public find-therapist URL param `languages`) */
  languages?: string;
  /** Comma-separated list (matches public find-therapist URL param `modalities`) */
  modalities?: string;
}

// Define the response structure from backend
interface TherapistResponse {
  data: Therapist[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export const fetchTherapists = createAsyncThunk<TherapistResponse, FetchTherapistsParams>(
  'therapist/fetchTherapists',
  async (
    {
      search,
      speciality,
      zipCode,
      page = 1,
      limit = 12,
      availability,
      insurance,
      sessionRate,
      sessionTypes,
      sessionMode,
      languages,
      modalities,
    },
    { rejectWithValue }
  ) => {

    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (speciality) params.append('speciality', speciality);
      if (page > 1) params.append('page', page.toString());
      if (limit !== 12) params.append('limit', limit.toString());
      if (zipCode) params.append('zipCode', zipCode);
      if (availability) params.append('availability', availability);
      if (insurance) params.append('insurance', insurance);
      if (sessionRate) params.append('sessionRate', sessionRate);
      if (sessionTypes) params.append('sessionTypes', sessionTypes);
      if (sessionMode) params.append('sessionMode', sessionMode);
      if (languages) params.append('languages', languages);
      if (modalities) params.append('modalities', modalities);

      const response = await api.get(`/search?${params.toString()}`);
      
      // Support direct JSON and Lambda proxy-wrapped responses.
      let responseBody;
      try {
        const parseMaybeJson = (value: unknown): unknown => {
          if (typeof value !== 'string') return value;
          try {
            return JSON.parse(value);
          } catch {
            return value;
          }
        };

        const firstParsed = parseMaybeJson(response.data);
        const unwrapped = (
          firstParsed &&
          typeof firstParsed === 'object' &&
          'body' in (firstParsed as Record<string, unknown>)
        )
          ? parseMaybeJson((firstParsed as Record<string, unknown>).body)
          : firstParsed;

        responseBody = unwrapped as Record<string, unknown>;

        const rawData =
          responseBody?.results ??
          responseBody?.data ??
          responseBody?.items;

        const data = Array.isArray(rawData)
          ? (rawData as Therapist[])
          : Array.isArray((rawData as { items?: unknown[] })?.items)
            ? ((rawData as { items: Therapist[] }).items)
            : [];

        const currentPage = Number(responseBody?.page || page);
        const total = Number(responseBody?.total || data.length);
        const totalPages = Number(
          responseBody?.totalPages ||
          (total > 0 ? Math.ceil(total / Number(responseBody?.limit || limit || 12)) : 1)
        );
        const hasNextPage = Boolean(responseBody?.hasNextPage ?? (currentPage < totalPages));
        const hasPrevPage = Boolean(responseBody?.hasPrevPage ?? (currentPage > 1));

        const paginationData = (responseBody?.pagination as TherapistResponse['pagination']) || {
          total,
          page: currentPage,
          limit: Number(responseBody?.limit || limit),
          totalPages,
          hasNextPage,
          hasPrevPage
        };
        
        return {
          data,
          pagination: paginationData
        };
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Failed to parse response data');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch therapists');
    }
  }
);

const therapistSlice = createSlice({
  name: 'therapist',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTherapists.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTherapists.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.therapist = action.payload.data;
        state.pagination = action.payload.pagination;
        state.loading = false;
      })
      .addCase(fetchTherapists.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

export default therapistSlice.reducer;
